import React, { useEffect, useState } from 'react';
import { Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

const SECTIONS = [
  {
    key: 'general', label: 'General',
    fields: [
      { key: 'store_name', label: 'Store name', type: 'text' },
      { key: 'contact_email', label: 'Contact email', type: 'email' },
      { key: 'contact_phone', label: 'Contact phone', type: 'text' },
      { key: 'currency', label: 'Currency', type: 'text' },
      { key: 'moderation_required_reviews', label: 'Require moderation for reviews', type: 'bool' },
      { key: 'moderation_required_questions', label: 'Require moderation for Q&A', type: 'bool' },
    ],
  },
  {
    key: 'seo_defaults', label: 'SEO',
    fields: [
      { key: 'meta_title', label: 'Default meta title', type: 'text' },
      { key: 'meta_description', label: 'Default meta description', type: 'textarea' },
      { key: 'og_image', label: 'Default OG image URL', type: 'text' },
      { key: 'sitemap_enabled', label: 'Enable sitemap', type: 'bool' },
    ],
  },
  {
    key: 'notifications', label: 'Notifications',
    fields: [
      { key: 'whatsapp_enabled', label: 'WhatsApp notifications enabled', type: 'bool' },
      { key: 'email_enabled', label: 'Email notifications enabled', type: 'bool' },
      { key: 'admin_notify_email', label: 'Admin notification email', type: 'email' },
    ],
    note: 'Actual delivery is controlled by env credentials (Phase 4).',
  },
  {
    key: 'shipping', label: 'Shipping',
    fields: [
      { key: 'free_shipping_threshold', label: 'Free shipping above (₹) — 0 = always free', type: 'number' },
      { key: 'base_shipping', label: 'Base shipping fee (₹)', type: 'number' },
      { key: 'default_eta_days', label: 'Default ETA (days)', type: 'number' },
      { key: 'min_order_value', label: 'Minimum order value (₹) — 0 = no minimum', type: 'number' },
    ],
    note: 'These rules apply instantly on the cart, checkout and order totals.',
  },
  {
    key: 'payments', label: 'Payments',
    fields: [
      { key: 'cod_enabled', label: 'Cash on Delivery (COD) enabled', type: 'bool' },
      { key: 'cod_fee', label: 'COD fee (₹, flat per order) — 0 = none', type: 'number' },
      { key: 'cod_max_order', label: 'COD max order total (₹) — 0 = no limit', type: 'number' },
      { key: 'razorpay_enabled', label: 'Online payment (Razorpay) enabled', type: 'bool' },
    ],
    note: 'Disabled methods disappear from checkout. Live Razorpay needs API keys via environment; without keys it runs in demo mode.',
  },
  {
    key: 'gift_wrap', label: 'Gift Wrap',
    fields: [
      { key: 'enabled', label: 'Offer gift wrap at checkout', type: 'bool' },
      { key: 'fee', label: 'Gift wrap fee (₹, flat per order)', type: 'number' },
      { key: 'title', label: 'Checkout title', type: 'text' },
      { key: 'description', label: 'Checkout description', type: 'textarea' },
    ],
  },
];

function Field({ f, value, onChange }) {
  const base = 'w-full rounded-lg border border-border px-3 py-2 text-sm outline-none';
  if (f.type === 'bool') return (
    <label className="inline-flex items-center gap-2 text-sm"><input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} data-testid={`setting-${f.key}`} /> {f.label}</label>
  );
  if (f.type === 'textarea') return (
    <div><label className="text-xs font-medium">{f.label}</label><textarea rows={3} value={value ?? ''} onChange={(e) => onChange(e.target.value)} className={base} data-testid={`setting-${f.key}`} /></div>
  );
  return (
    <div><label className="text-xs font-medium">{f.label}</label><input type={f.type} value={value ?? ''} onChange={(e) => onChange(f.type === 'number' ? Number(e.target.value) : e.target.value)} className={base} data-testid={`setting-${f.key}`} /></div>
  );
}

export default function AdminSettings() {
  const [tab, setTab] = useState('general');
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(false);

  const load = (section) => {
    setLoading(true);
    api.get(`/admin/settings/${section}`).then((r) => setValues((v) => ({ ...v, [section]: r.data.values }))).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { if (!values[tab]) load(tab); /* eslint-disable-next-line */ }, [tab]);

  const save = async (section) => {
    try {
      const { data } = await api.put(`/admin/settings/${section}`, { values: values[section] || {} });
      setValues((v) => ({ ...v, [section]: data.values }));
      toast.success('Settings saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  return (
    <div>
      <h1 className="text-2xl font-semibold">Settings</h1>
      <p className="text-sm text-muted-foreground">Central place for store, SEO, notifications, shipping and payments configuration.</p>

      <Tabs value={tab} onValueChange={setTab} className="mt-4">
        <TabsList className="flex flex-wrap gap-1 bg-transparent p-0">
          {SECTIONS.map((s) => (
            <TabsTrigger key={s.key} value={s.key} data-testid={`settings-tab-${s.key}`} className="rounded-full border border-border bg-white px-4 py-1.5 text-xs data-[state=active]:bg-[hsl(var(--primary))] data-[state=active]:text-white">{s.label}</TabsTrigger>
          ))}
        </TabsList>
        {SECTIONS.map((s) => (
          <TabsContent key={s.key} value={s.key} className="mt-4">
            <div className="rounded-2xl border border-border bg-white p-6">
              <div className="grid gap-4 sm:grid-cols-2">
                {s.fields.map((f) => (
                  <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                    <Field f={f} value={(values[s.key] || {})[f.key]} onChange={(val) => setValues((v) => ({ ...v, [s.key]: { ...(v[s.key] || {}), [f.key]: val } }))} />
                  </div>
                ))}
              </div>
              {s.note && <p className="mt-3 text-xs text-muted-foreground">{s.note}</p>}
              <button onClick={() => save(s.key)} disabled={loading} data-testid={`settings-save-${s.key}`} className="mt-5 inline-flex items-center gap-2 rounded-full bg-[hsl(var(--primary))] px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                <Save size={14} /> Save {s.label}
              </button>
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
